// src/pages/AdminPage/tabs/ReportsTab.tsx
import { useEffect, useState, useCallback } from "react";
import { Flag, AlertTriangle, SpellCheck, MessageSquare } from "lucide-react";
import { adminAPI } from "../utils/adminApi";
import type {
  QuestionReportItem,
  QuestionReportStatus,
  QuestionReportType,
} from "../utils/adminTypes";

interface ReportsTabProps {
  onStatusChange: (id: number, status: QuestionReportStatus) => void;
  onError: (msg: string) => void;
}

const STATUS_TABS: { key: QuestionReportStatus | "all"; label: string }[] = [
  { key: "all", label: "Barchasi" },
  { key: "pending", label: "Kutilmoqda" },
  { key: "reviewed", label: "Ko'rib chiqilgan" },
  { key: "resolved", label: "Hal qilingan" },
];

const STATUS_COLORS: Record<QuestionReportStatus, { bg: string; text: string }> =
  {
    pending: { bg: "rgba(245,158,11,.12)", text: "#F59E0B" },
    reviewed: { bg: "rgba(59,130,246,.12)", text: "#3B82F6" },
    resolved: { bg: "rgba(34,197,94,.12)", text: "#22C55E" },
  };

const TYPE_META: Record<
  QuestionReportType,
  { icon: any; label: string; color: string }
> = {
  wrong_answer: { icon: AlertTriangle, label: "Javob xato", color: "#EF4444" },
  typo: { icon: SpellCheck, label: "Imloviy xato", color: "#F59E0B" },
  comment: { icon: MessageSquare, label: "Izoh", color: "#3B82F6" },
};

export default function ReportsTab({ onStatusChange, onError }: ReportsTabProps) {
  const [items, setItems] = useState<QuestionReportItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<QuestionReportStatus | "all">(
    "all",
  );
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    adminAPI
      .getReports(statusFilter === "all" ? undefined : statusFilter)
      .then((res) => setItems(res.data || []))
      .catch(() => onError("Shikoyatlarni yuklashda xatolik"))
      .finally(() => setLoading(false));
  }, [statusFilter, onError]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="fade-up">
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 16,
          flexWrap: "wrap",
        }}>
        {STATUS_TABS.map((s) => (
          <button
            key={s.key}
            onClick={() => setStatusFilter(s.key)}
            style={{
              padding: "9px 14px",
              borderRadius: 10,
              border:
                statusFilter === s.key ?
                  "1px solid rgba(59,130,246,.4)"
                : "1px solid #1E2D45",
              background:
                statusFilter === s.key ? "rgba(59,130,246,.12)" : "#111827",
              color: statusFilter === s.key ? "#3B82F6" : "#64748B",
              fontWeight: 700,
              fontSize: 12,
              cursor: "pointer",
            }}>
            {s.label}
          </button>
        ))}
        <div
          style={{
            marginLeft: "auto",
            color: "#64748B",
            fontSize: 13,
            padding: "9px 14px",
            background: "#111827",
            border: "1px solid #1E2D45",
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}>
          <Flag size={14} /> Jami:{" "}
          <strong style={{ color: "#fff" }}>{items.length}</strong>
        </div>
      </div>

      {loading ?
        <p style={{ color: "#64748B", fontSize: 13 }}>Yuklanmoqda...</p>
      : items.length === 0 ?
        <p style={{ color: "#64748B", fontSize: 13 }}>Shikoyatlar topilmadi</p>
      : <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {items.map((r) => {
            const sc = STATUS_COLORS[r.status];
            return (
              <div
                key={r.id}
                style={{
                  padding: 14,
                  background: "#111827",
                  border: "1px solid #1E2D45",
                  borderRadius: 14,
                }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 12,
                    marginBottom: 8,
                  }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        color: "#E2E8F0",
                        fontWeight: 700,
                        fontSize: 13,
                        marginBottom: 2,
                      }}>
                      Savol #{r.question?.id}
                      {r.question?.ticket && ` — Bilet ${r.question.ticket.id}`}
                    </div>
                    {r.question?.question_uz && (
                      <p
                        style={{
                          color: "#64748B",
                          fontSize: 12,
                          margin: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}>
                        {r.question.question_uz}
                      </p>
                    )}
                  </div>
                  <span
                    style={{
                      padding: "4px 10px",
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 700,
                      background: sc.bg,
                      color: sc.text,
                      flexShrink: 0,
                    }}>
                    {STATUS_TABS.find((s) => s.key === r.status)?.label}
                  </span>
                </div>

                <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                  {r.types.map((t) => {
                    const meta = TYPE_META[t];
                    const Icon = meta.icon;
                    return (
                      <span
                        key={t}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          padding: "3px 8px",
                          borderRadius: 8,
                          fontSize: 11,
                          fontWeight: 600,
                          background: `${meta.color}1F`,
                          color: meta.color,
                        }}>
                        <Icon size={11} /> {meta.label}
                      </span>
                    );
                  })}
                </div>

                {r.comment && (
                  <p
                    style={{
                      color: "#94A3B8",
                      fontSize: 12,
                      lineHeight: 1.5,
                      margin: "0 0 8px",
                      background: "#0D1526",
                      padding: "8px 10px",
                      borderRadius: 8,
                    }}>
                    {r.comment}
                  </p>
                )}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 12,
                    flexWrap: "wrap",
                  }}>
                  <span style={{ color: "#475569", fontSize: 11 }}>
                    {r.user?.name || r.user?.telegram_username || r.user?.id}
                    {" · "}
                    {new Date(r.createdAt).toLocaleString("uz-UZ")}
                  </span>
                  <select
                    value={r.status}
                    onChange={(e) =>
                      onStatusChange(r.id, e.target.value as QuestionReportStatus)
                    }
                    style={{
                      padding: "6px 10px",
                      borderRadius: 8,
                      border: "1px solid #1E2D45",
                      background: "#0D1526",
                      color: "#E2E8F0",
                      fontSize: 12,
                      cursor: "pointer",
                    }}>
                    <option value="pending">Kutilmoqda</option>
                    <option value="reviewed">Ko'rib chiqilgan</option>
                    <option value="resolved">Hal qilingan</option>
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      }
    </div>
  );
}
